import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';

export default function MessagingSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['messaging-settings'],
    queryFn: () => apiService.getMessagingSettingsAdmin(),
  });

  const [loserTemplates, setLoserTemplates] = useState<string[]>([]);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (!data) return;
    setLoserTemplates([...(data.loser_message_templates ?? [])]);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const normalized = loserTemplates.map((line) => line.trim()).filter((line) => line !== '');
      return apiService.updateMessagingSettingsAdmin({
        loser_message_templates: normalized.length > 0 ? normalized : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messaging-settings'] });
      setSaveSuccess(true);
      window.setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const handleAddTemplate = () => {
    if (loserTemplates.length >= 20) return;
    setLoserTemplates((prev) => [...prev, '']);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Messages messagerie
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Messages type proposés au <strong>perdeur</strong> dans la messagerie (réponses rapides générales,
        indépendantes des catégories). Les messages pour le trouveur se configurent dans chaque catégorie.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les réglages messagerie.
        </Alert>
      )}

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Messages enregistrés.
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Messages type perdeur
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Raccourcis affichés au-dessus du champ de saisie lorsque l&apos;utilisateur est le propriétaire
            de l&apos;objet perdu (perdeur).
          </Typography>

          <Stack spacing={1.5}>
            {loserTemplates.map((template, index) => (
              <Box key={`loser-template-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  label={`Message ${index + 1}`}
                  value={template}
                  onChange={(e) => {
                    const next = [...loserTemplates];
                    next[index] = e.target.value;
                    setLoserTemplates(next);
                  }}
                  placeholder="Ex. Bonjour, pouvez-vous me décrire l'objet pour confirmer qu'il s'agit bien du mien ?"
                />
                <IconButton
                  aria-label="Supprimer ce message type"
                  onClick={() => setLoserTemplates((prev) => prev.filter((_, i) => i !== index))}
                  sx={{ mt: 1 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddTemplate}
              disabled={loserTemplates.length >= 20}
              sx={{ alignSelf: 'flex-start' }}
            >
              Ajouter un message type
            </Button>
          </Stack>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </Box>

          {saveMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Impossible d&apos;enregistrer les messages.
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
}
